import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Header, Footer } from "@/components/SiteChrome";
import { cart } from "@/lib/cart";
import api from "@/lib/api";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState({ payment_status: "pending", status: "initiated" });
    const [attempts, setAttempts] = useState(0);
    const clearedRef = useRef(false);

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                setStatus(data);
                if (data.payment_status === "paid") {
                    if (!clearedRef.current) {
                        cart.clear();
                        clearedRef.current = true;
                    }
                    return;
                }
                if (data.payment_status === "failed" || data.payment_status === "expired") return;
                if (attempts < 15) {
                    setAttempts((a) => a + 1);
                    setTimeout(poll, 2000);
                }
            } catch {
                if (attempts < 15) setTimeout(poll, 2000);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId, attempts]);

    const paid = status.payment_status === "paid";
    const failed = status.payment_status === "failed" || status.payment_status === "expired";

    return (
        <div className="min-h-screen bg-[hsl(var(--dust-50))]" data-testid="payment-success-page">
            <Header />
            <div className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-6">
                <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">Checkout</div>
                {paid ? (
                    <>
                        <h1 className="mt-4 font-display text-6xl uppercase italic text-[hsl(var(--success))]" data-testid="payment-paid">
                            Loaded. Ready to roll.
                        </h1>
                        <p className="mt-6 text-[hsl(var(--ink-soft))]">
                            Your order is confirmed. A receipt is on the way.
                        </p>
                        {status.amount && (
                            <div className="mt-6 font-display text-4xl text-[hsl(var(--ink))]">${Number(status.amount).toFixed(2)}</div>
                        )}
                    </>
                ) : failed ? (
                    <>
                        <h1 className="mt-4 font-display text-6xl uppercase italic text-[hsl(var(--danger))]">
                            Payment didn't clear.
                        </h1>
                        <p className="mt-6 text-[hsl(var(--ink-soft))]">
                            No charge was made. Try again or reach out.
                        </p>
                    </>
                ) : (
                    <>
                        <h1 className="mt-4 font-display text-6xl uppercase italic text-[hsl(var(--ink))]">
                            Verifying payment…
                        </h1>
                        <p className="mt-6 text-[hsl(var(--ink-soft))]">Give us a moment — this usually takes 2–5 seconds.</p>
                    </>
                )}
                <div className="mt-10 flex justify-center gap-4">
                    <Link to="/shop" className="btn-primary">Keep building →</Link>
                    <Link to="/" className="btn-ghost">Home</Link>
                </div>
                <div className="mt-8 font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                    session: {sessionId?.slice(0, 20)}…
                </div>
            </div>
            <Footer />
        </div>
    );
}
